import { useState } from "react";
import { Link } from "react-router-dom";
import { Stack, Typography } from "@mui/material";
import { toast } from "react-toastify";
import AuthShell, {
    AuthField,
    AuthSubmit,
    BuildingIcon,
    LockIcon,
    MailIcon,
    PersonIcon,
    ShieldNote,
} from "../components/AuthShell";
import OtpStep from "../components/OtpStep";
import { signup } from "../api";

/**
 * Create a workspace — same split-screen shell as Sign in, then the shared
 * OTP step, then /dashboard.
 */
export default function SignUp() {
    const [step, setStep] = useState("details"); // details | otp
    const [form, setForm] = useState({
        organizationName: "",
        firstName: "",
        lastName: "",
        email: "",
        password: "",
    });
    const [pendingToken, setPendingToken] = useState("");
    const [otpEmail, setOtpEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const set = (key) => (e) =>
        setForm((prev) => ({ ...prev, [key]: e.target.value }));

    async function handleSubmit(e) {
        e.preventDefault();
        if (form.password.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        setLoading(true);
        try {
            const body = {
                organizationName: form.organizationName.trim(),
                firstName: form.firstName.trim(),
                email: form.email.trim(),
                password: form.password,
            };
            if (form.lastName.trim()) body.lastName = form.lastName.trim();

            const { data } = await signup(body);
            if (data?.otpRequired && data?.pendingToken) {
                setPendingToken(data.pendingToken);
                setOtpEmail(data.email || form.email);
                setStep("otp");
                if (data.message) toast.success(data.message);
            } else {
                toast.error("Unexpected response from the server");
            }
        } catch (err) {
            toast.error(
                err.response?.data?.error ||
                    err.response?.data?.message ||
                    "Unable to create your workspace",
            );
        } finally {
            setLoading(false);
        }
    }

    if (step === "otp") {
        return (
            <AuthShell
                headline={
                    <>
                        One code away from <em>your</em> workspace.
                    </>
                }
                sub="We verify every sign-in with a one-time code, so your customer data stays exactly where it belongs."
                bottomSlot={<ShieldNote />}
            >
                <OtpStep
                    email={otpEmail}
                    pendingToken={pendingToken}
                    setPendingToken={setPendingToken}
                    onBack={() => setStep("details")}
                />
            </AuthShell>
        );
    }

    return (
        <AuthShell
            headline={
                <>
                    One workspace. Every process <em>you</em> run.
                </>
            }
            sub="Create your organisation, invite your team and shape the CRM around the way you already work."
        >
            <Stack
                component="form"
                onSubmit={handleSubmit}
                sx={{ width: "100%", maxWidth: 420, gap: 4 }}
            >
                <Stack sx={{ gap: "10px" }}>
                    <Typography
                        sx={{
                            fontFamily: "'Newsreader', Georgia, serif",
                            fontSize: 38,
                            fontWeight: 400,
                            letterSpacing: "-0.5px",
                        }}
                    >
                        Create your workspace
                    </Typography>
                    <Typography
                        sx={{ fontSize: 15, color: "#6E6B66", lineHeight: 1.6 }}
                    >
                        It&#39;s free to start. We&#39;ll email you a one-time
                        code to verify your address.
                    </Typography>
                </Stack>

                <Stack sx={{ gap: "20px" }}>
                    <AuthField
                        label="ORGANIZATION NAME"
                        icon={<BuildingIcon />}
                        type="text"
                        required
                        autoFocus
                        placeholder="Meera Constructions"
                        value={form.organizationName}
                        onChange={set("organizationName")}
                    />
                    <Stack
                        sx={{
                            flexDirection: { xs: "column", sm: "row" },
                            gap: "16px",
                            "& > *": { flex: 1 },
                        }}
                    >
                        <AuthField
                            label="FIRST NAME"
                            icon={<PersonIcon />}
                            type="text"
                            required
                            placeholder="Priya"
                            value={form.firstName}
                            onChange={set("firstName")}
                        />
                        <AuthField
                            label="LAST NAME (OPTIONAL)"
                            icon={<PersonIcon />}
                            type="text"
                            placeholder="Raman"
                            value={form.lastName}
                            onChange={set("lastName")}
                        />
                    </Stack>
                    <AuthField
                        label="WORK EMAIL"
                        icon={<MailIcon />}
                        type="email"
                        required
                        autoComplete="email"
                        placeholder="you@company.com"
                        value={form.email}
                        onChange={set("email")}
                    />
                    <AuthField
                        label="PASSWORD"
                        icon={<LockIcon />}
                        type="password"
                        required
                        minLength={6}
                        autoComplete="new-password"
                        placeholder="At least 6 characters"
                        value={form.password}
                        onChange={set("password")}
                    />
                </Stack>

                <AuthSubmit loading={loading}>
                    {loading ? "Creating workspace…" : "Continue"}
                </AuthSubmit>

                <Typography
                    sx={{
                        fontSize: 14,
                        color: "#6E6B66",
                        textAlign: "center",
                    }}
                >
                    Already have a workspace?{" "}
                    <Typography
                        component={Link}
                        to="/signin"
                        sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#171715",
                            borderBottom: "1px solid #171715",
                            pb: "1px",
                            "&:hover": { color: "#1E6B4F" },
                        }}
                    >
                        Sign in
                    </Typography>
                </Typography>
            </Stack>
        </AuthShell>
    );
}
